import React from "react";
import ReactDropzone from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TDispatch } from "../../..";
import { IAppState } from '../../../interfaces';
import * as actions from "../../../store/actions";
import Alert from "../Alert/Alert";
import Spinner from "../Spinner/Spinner";
import classes from "./ImageDropzone.module.scss";
import { ETranslation } from "../../../translations/translation-keys";

export interface IDropzoneConfig {
  text?: string;
  dragText?: string;
}

interface IProps {
  className?: string;
  config?: IDropzoneConfig;
  organizationId?: string;
  value?: string;
  onChange?: (base64: string) => void;
  disabled?: boolean;
}

const ImageDropzone: React.FC<IProps> = ({
  className,
  config = {},
  organizationId,
  value,
  onChange,
  disabled
}) => {
  const [timestamp, setTimestamp] = React.useState(Date.now());
  const [imageFetchError, setImageFetchError] = React.useState(false);
  const isMounted = React.useRef(true);
  
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  React.useEffect(() => {
    setImageFetchError(false);
  }, [timestamp, organizationId, value]);

  const { t } = useTranslation();
  const dispatch = useDispatch<TDispatch>();
  const { error, loading, currentOrganizationId } = useSelector((state: IAppState) => ({
    error: state.organization.imageError,
    loading: state.organization.imageLoading,
    currentOrganizationId: state.auth.authUser?.organization?.id,
  }));

  const onDropHandler = async (files: File[]) => {
    if (disabled) return;
    
    if (onChange) {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          onChange(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
      return;
    }

    const formData = new FormData();
    for (let file of files) {
      formData.append("files[]", file);
    }

    if (!formData.has("files[]")) return;
    await dispatch(actions.updateOrganizationImage(formData, organizationId));
    if (isMounted.current) {
      setTimestamp(Date.now());
      setImageFetchError(false);
    }
  };

  const hostname = window && window.location && window.location.hostname;
  let apiPath = "/";
  if (hostname === "localhost") {
    apiPath = "http://localhost:8080/";
  }
  const targetId = organizationId || currentOrganizationId;
  
  const previewUrl = value ? value : (targetId ? `${apiPath}json/organizations/image?t=${timestamp}&id=${targetId}` : null);

  const defaultText = t(ETranslation.ORGANIZATION_IMAGE_DROPZONE);
  const defaultDragText = t(ETranslation.ORGANIZATION_IMAGE_DROPZONE_DROP);

  return (
    <>
      {error && !onChange && <Alert>{t(error)}</Alert>}
      <ReactDropzone onDrop={onDropHandler} accept={"image/png,image/jpg,image/jpeg"} disabled={disabled}>
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div className={className || classes.Dropzone} {...getRootProps()}>
              <input {...getInputProps()} />
              <div className={classes.Container}>
                {loading && !onChange ? (
                  <Spinner />
                ) : (
                  previewUrl && !imageFetchError && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img 
                        src={previewUrl} 
                        alt="Logo preview" 
                        className={classes.PreviewImage} 
                        onError={() => setImageFetchError(true)} 
                      />
                      <button
                        style={{ position: 'absolute', top: '5px', right: '5px', cursor: 'pointer', padding: '2px 8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (disabled) return;
                          
                          if (onChange) {
                            onChange("");
                          } else if (targetId) {
                            dispatch(actions.deleteOrganizationImage(targetId));
                            if (isMounted.current) {
                              setTimestamp(Date.now());
                              setImageFetchError(true);
                            }
                          }
                        }}
                      >
                        Poista
                      </button>
                    </div>
                  )
                )}
                <p className={classes.Text}>{isDragActive ? (config.dragText || defaultDragText) : (config.text || defaultText)}</p>
              </div>
            </div>
          );
        }}
      </ReactDropzone>
    </>
  );
};

export default ImageDropzone;
